import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Buildings, CheckCircle, ArrowRight, User, Envelope, Phone, Briefcase, Users, ShieldCheck, SealCheck, CircleNotch } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Layout } from '../components/layout/Layout';
import { API_URL, formatApiErrorDetail } from '../lib/utils';
import { LiquidGlassBg } from '../components/ui/LiquidGlassComponents';

const industries = [
  'Technology / IT',
  'Manufacturing',
  'Healthcare',
  'Finance / Banking',
  'Retail',
  'Education',
  'Telecommunications',
  'Construction',
  'Other'
];

const employeeCounts = [
  '1-50',
  '51-100',
  '101-250',
  '251-500',
  '501-1000',
  '1000+'
];

const InputWrapper = ({ label, icon: Icon, children }) => (
  <div className="space-y-2">
    <Label className="text-[10px] font-black text-white/30 uppercase tracking-widest ml-1">{label}</Label>
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20 group-focus-within:text-[#0048ff] transition-colors">
        <Icon size={20} weight="duotone" />
      </div>
      {children}
    </div>
  </div>
);

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    employees: '',
    industry: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: formData.company_name,
          contact_name: formData.contact_name,
          email: formData.email,
          phone: formData.phone,
          employees: parseInt(formData.employees.split('-')[0]) || null,
          source: 'register',
          industry: formData.industry,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(formatApiErrorDetail(err.detail));
      }

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  if (success) {
    return (
      <Layout>
        <section className="min-h-screen bg-[#030712] relative overflow-hidden flex items-center pt-20">
          <LiquidGlassBg />
          <div className="container-wide relative z-10 px-4">
            <motion.div 
              className="max-w-xl mx-auto glass-panel p-12 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="w-24 h-24 rounded-3xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
                <SealCheck size={56} className="text-emerald-500" weight="duotone" />
              </div>
              <h1 className="text-4xl font-black text-white mb-6 tracking-tight uppercase">Registration Successful</h1>
              <p className="text-white/40 text-lg mb-10 leading-relaxed">
                Thank you for onboarding <span className="text-white font-bold">{formData.company_name}</span>. Our structural engineering team will reach out within 24 hours to finalize your healthcare architecture.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/calculators/quick">
                  <Button className="h-14 px-8 rounded-2xl bg-[#0048ff] hover:bg-[#003bce] text-white font-black uppercase tracking-widest text-xs gap-3">
                    Analyze Cost Savings <ArrowRight weight="bold" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="h-14 px-8 rounded-2xl border-white/10 bg-white/5 text-white font-black uppercase tracking-widest text-xs">
                    Return to Portal
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="min-h-screen bg-[#030712] relative overflow-hidden pt-32 pb-20">
        <LiquidGlassBg />
        
        <div className="container-wide relative z-10 px-4 md:px-8">
          <div className="max-w-5xl mx-auto flex flex-col lg:flex-row gap-16 items-center">
            {/* Context Side */}
            <div className="flex-1 lg:max-w-md text-center lg:text-left">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0048ff]/10 text-[#0048ff] text-xs font-bold uppercase tracking-widest mb-8">
                <ShieldCheck size={16} weight="bold" /> Enterprise Onboarding
              </div>
              <h1 className="text-5xl md:text-6xl font-black text-white mb-8 leading-[1.1] tracking-tight">Register Your Organization</h1>
              <p className="text-white/40 text-lg leading-relaxed mb-10">
                Begin your journey towards a smarter, multi-rail healthcare infrastructure. Centralize governance, optimize costs, and empower your workforce.
              </p>
              
              <div className="space-y-6 hidden lg:block">
                {[
                  { title: "Direct Rail Integration", sub: "Instant access to corporate health networks." },
                  { title: "Smart Cost Optimization", sub: "AI-driven insights for maximum fiscal efficiency." },
                  { title: "Seamless Governance", sub: "Real-time visibility into claim pipelines." }
                ].map((item, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#0048ff] flex-shrink-0">
                      <CheckCircle size={20} weight="duotone" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm tracking-wide">{item.title}</h4>
                      <p className="text-white/30 text-xs mt-1">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Side */}
            <div className="w-full max-w-xl">
              <motion.div 
                className="glass-panel p-8 md:p-12 relative"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
              >
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-[#0048ff]/10 blur-3xl rounded-full" />
                
                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-6">
                    <InputWrapper label="Company Name" icon={Buildings}>
                      <Input
                        required
                        value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="e.g., Tesla Inc."
                      />
                    </InputWrapper>
                    <InputWrapper label="Contact Person" icon={User}>
                      <Input
                        required
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="e.g., Elon Musk"
                      />
                    </InputWrapper>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <InputWrapper label="Email Address" icon={Envelope}>
                      <Input
                        required
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="name@company.com"
                      />
                    </InputWrapper>
                    <InputWrapper label="Phone Number" icon={Phone}>
                      <Input
                        required
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        className="h-14 pl-12 bg-white/5 border-white/10 focus:border-[#0048ff] text-white rounded-xl placeholder:text-white/20"
                        placeholder="+92 3XX XXXXXXX"
                      />
                    </InputWrapper>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <InputWrapper label="Industry Sector" icon={Briefcase}>
                      <Select 
                        value={formData.industry} 
                        onValueChange={(v) => setFormData({...formData, industry: v})}
                      >
                        <SelectTrigger className="h-14 pl-12 bg-white/5 border-white/10 focus:ring-[#0048ff] text-white rounded-xl">
                          <SelectValue placeholder="Select Sector" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                          {industries.map(ind => (
                            <SelectItem key={ind} value={ind} className="hover:bg-white/5 focus:bg-white/5">{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </InputWrapper>
                    <InputWrapper label="Employee Lives" icon={Users}>
                      <Select 
                        value={formData.employees} 
                        onValueChange={(v) => setFormData({...formData, employees: v})}
                      >
                        <SelectTrigger className="h-14 pl-12 bg-white/5 border-white/10 focus:ring-[#0048ff] text-white rounded-xl">
                          <SelectValue placeholder="Workforce Size" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0f172a] border-white/10 text-white">
                          {employeeCounts.map(count => (
                            <SelectItem key={count} value={count} className="hover:bg-white/5 focus:bg-white/5">{count}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </InputWrapper>
                  </div>

                  {error && (
                    <motion.div 
                      initial={{ opacity: 0, y: -10 }} 
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold uppercase tracking-widest text-center"
                    >
                      {error}
                    </motion.div>
                  )}

                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="w-full h-16 rounded-2xl bg-[#0048ff] hover:bg-[#003bce] text-white font-black uppercase tracking-[0.2em] text-sm shadow-[0_20px_50px_rgba(0,72,255,0.3)] transition-all flex gap-4"
                  >
                    {loading ? (
                      <>
                        <CircleNotch size={20} className="animate-spin" /> Finalizing Account...
                      </>
                    ) : (
                      <>
                        Initialize Onboarding <ArrowRight weight="bold" />
                      </>
                    )}
                  </Button>

                  <p className="text-[10px] text-white/20 text-center uppercase tracking-[0.2em] font-medium leading-relaxed">
                    By registering, you agree to our Enterprise Protocols <br/> and Strategic Access Terms.
                  </p>
                </form>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
